import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, FlatList, TextInput, Platform, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { Input } from './Input';
import { colors, spacing, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { INDUSTRIES, isIndustryCode } from '../../constants/industries';

interface IndustryPickerProps {
  /** An industry code, 'other', or free text typed under "Other". */
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
}

/**
 * Searchable industry list (same look as CountryPicker: inline dropdown on
 * web, bottom sheet on native). "Other" reveals a text field; what's typed
 * there is stored as the industry.
 */
export const IndustryPicker: React.FC<IndustryPickerProps> = ({ value, onChange, label, error }) => {
  const styles = useThemedStyles(getStyles);
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // 'other' itself, or any value that isn't a known code (typed text)
  const isOther = value === 'other' || (!!value && !isIndustryCode(value));
  const otherText = isOther && value !== 'other' ? value : '';
  const selectedCode = isOther ? 'other' : value;

  const items = useMemo(
    () => INDUSTRIES.map((i) => ({ ...i, label: t(`industry.${i.code}` as any) })),
    [t]
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((i) => i.label.toLowerCase().includes(q) || i.code.includes(q)) : items;
  }, [items, search]);

  const selected = items.find((i) => i.code === selectedCode);
  const display = selected ? selected.label : t('industry.select');

  const pick = (code: string) => {
    // Keep typed text when re-picking Other
    onChange(code === 'other' ? (otherText || 'other') : code);
    setOpen(false);
    setSearch('');
  };

  const row = (item: typeof items[number]) => {
    const isSelected = item.code === selectedCode;
    return (
      <TouchableOpacity
        key={item.code}
        style={[styles.item, { flexDirection: rtl.rowDirection }, isSelected && styles.itemSelected]}
        onPress={() => pick(item.code)}
        activeOpacity={0.7}
      >
        <Ionicons name={item.icon as any} size={18} color={(isSelected ? colors.primary : colors.textLight) as string} />
        <Text style={[styles.itemName, { textAlign: rtl.textStart }, isSelected && styles.itemNameSelected]} numberOfLines={1}>{item.label}</Text>
        {isSelected && <Ionicons name="checkmark" size={18} color={colors.primary as string} />}
      </TouchableOpacity>
    );
  };

  const searchBox = (style: any) => (
    <View style={[style, { flexDirection: rtl.rowDirection }]}>
      <Ionicons name="search-outline" size={15} color={colors.textLight as string} />
      <TextInput
        style={[Platform.OS === 'web' ? styles.webSearchInput : styles.searchInput, { textAlign: rtl.textStart }]}
        placeholder={t('industry.search')}
        placeholderTextColor={colors.textLight as string}
        value={search}
        onChangeText={setSearch}
        autoCorrect={false}
        autoCapitalize="none"
        autoFocus={Platform.OS === 'web'}
      />
      {search.length > 0 && (
        <TouchableOpacity onPress={() => setSearch('')}>
          <Ionicons name="close-circle" size={15} color={colors.textLight as string} />
        </TouchableOpacity>
      )}
    </View>
  );

  const trigger = (
    <TouchableOpacity
      style={[styles.trigger, { flexDirection: rtl.rowDirection }, error ? styles.triggerError : undefined, open && Platform.OS === 'web' && styles.triggerOpen]}
      onPress={() => setOpen((v) => !v)}
      activeOpacity={0.7}
      accessibilityRole="button"
    >
      {selected && <Ionicons name={selected.icon as any} size={18} color={colors.primary as string} />}
      <Text style={[styles.name, { textAlign: rtl.textStart }, !selected && styles.placeholder]} numberOfLines={1}>{display}</Text>
      <Ionicons name={open && Platform.OS === 'web' ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textLight as string} />
    </TouchableOpacity>
  );

  return (
    <View style={styles.wrapper}>
      {label ? <Text style={[styles.label, { textAlign: rtl.textStart }]}>{label}</Text> : null}
      {trigger}
      {Platform.OS === 'web' && open && (
        <View style={styles.webDropdown}>
          {searchBox(styles.webSearchRow)}
          <ScrollView style={styles.webList} nestedScrollEnabled showsVerticalScrollIndicator={false}>
            {filtered.map(row)}
            {filtered.length === 0 && <Text style={styles.emptyText}>{t('industry.none')}</Text>}
          </ScrollView>
        </View>
      )}
      {error && !isOther ? <Text style={styles.errorText}>{error}</Text> : null}
      {isOther && (
        <Input
          value={otherText}
          onChangeText={(text) => onChange(text.trim() ? text : 'other')}
          placeholder={t('industry.otherPlaceholder')}
          variant="outlined"
          error={error}
        />
      )}

      {Platform.OS !== 'web' && (
        <Modal visible={open} animationType="slide" transparent>
          <View style={styles.overlay}>
            <View style={styles.sheet}>
              <View style={[styles.sheetHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={styles.sheetTitle}>{t('industry.select')}</Text>
                <TouchableOpacity onPress={() => { setOpen(false); setSearch(''); }} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="close" size={22} color={colors.textDark as string} />
                </TouchableOpacity>
              </View>
              {searchBox(styles.searchRow)}
              <FlatList
                data={filtered}
                keyExtractor={(item) => item.code}
                renderItem={({ item }) => row(item)}
                ItemSeparatorComponent={() => <View style={styles.separator} />}
                keyboardShouldPersistTaps="handled"
                ListEmptyComponent={<Text style={styles.emptyText}>{t('industry.none')}</Text>}
              />
            </View>
          </View>
        </Modal>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  wrapper: { gap: 4 },
  label: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium, marginBottom: 4 },
  trigger: {
    alignItems: 'center',
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surface,
    gap: spacing.sm,
    minHeight: 44,
  },
  triggerOpen: { borderColor: colors.primary, borderBottomLeftRadius: 0, borderBottomRightRadius: 0, borderBottomWidth: 0 },
  triggerError: { borderColor: colors.error },
  name: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.textDark },
  placeholder: { color: colors.textLight },
  errorText: { fontSize: typography.fontSize.xs, color: colors.error, fontFamily: typography.fontFamily.regular },
  webDropdown: {
    borderWidth: 1.5,
    borderTopWidth: 0,
    borderColor: colors.primary,
    borderBottomLeftRadius: designTokens.borderRadius.md,
    borderBottomRightRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
    ...designTokens.shadows.sm,
  },
  webSearchRow: {
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: spacing.sm,
    backgroundColor: colors.lightGray,
  },
  webSearchInput: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.textDark, outlineStyle: 'none' } as any,
  webList: { maxHeight: 320 },
  emptyText: { padding: spacing.lg, textAlign: 'center', fontSize: typography.fontSize.sm, color: colors.textLight },
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: colors.background.surface, borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '80%', ...designTokens.shadows.lg },
  sheetHeader: {
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  sheetTitle: { fontSize: typography.fontSize.lg, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  searchRow: {
    alignItems: 'center',
    margin: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: Platform.OS === 'ios' ? spacing.sm : 0,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.lightGray,
    gap: spacing.sm,
  },
  searchInput: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.textDark, paddingVertical: Platform.OS === 'android' ? spacing.sm : 0 },
  item: { alignItems: 'center', paddingHorizontal: spacing.lg, paddingVertical: spacing.sm + 2, gap: spacing.md },
  itemSelected: { backgroundColor: (colors.primary as unknown as string) + '10' },
  itemName: { flex: 1, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.regular, color: colors.textDark },
  itemNameSelected: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  separator: { height: 1, backgroundColor: (colors.border as unknown as string) + '60' },
});
